import React, { FC, useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GenericObject, Nullable } from '@dxb-web-ui-framework/common/types';
import { makeStyles } from '@dxb-web-ui-framework/design-system';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { ClientSideWizardFlowPresenter } from '../../../../content-composer/src/components/ClientSideWizard/ClientSideWizardFlowPresenter';
import { ClientSideWizardStepper } from '../../../../content-composer/src/components/ClientSideWizard/ClientSideWizardStepper';
import { TransferServiceImpl } from '../../domain/Transfer/usecases';
import { useFundsTransferFlowConfig, useTransferStepperConfig } from './';

const initialFormState = (initialFundTransferFormData: Nullable<GenericObject>) => ({
  data: {
    initTransfer: {
      stepId: 'initTransfer',
      values: {
        ...initialFundTransferFormData,
      },
    },
    transferConfirm: {
      stepId: 'transferConfirm',
      values: {
        ...initialFundTransferFormData,
      },
    },
    transferOTP: {
      stepId: 'transferOTP',
      values: {
        ...initialFundTransferFormData,
      },
    },
  },
  currentStep: 'initTransfer',
  validationOptions: { shouldValidate: false, shouldDirty: true },
});

const useStyles = makeStyles(({ breakpoints, spacing }) => ({
  stepperWrapper: {
    flexBasis: 'unset',
    padding: 0,
    justifyContent: 'flex-start',
    maxWidth: '450px',
    width: '100%',
    [breakpoints.down('xs')]: {
      maxWidth: '250px',
    },
  },
  stepperRoot: {
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
    width: '100%',
    paddingBottom: spacing(4),
    [breakpoints.down('sm')]: {
      paddingLeft: spacing(2),
      paddingRight: spacing(2),
    },
  },
  clientSideWizardRootContainer: {
    paddingLeft: 0,
    paddingRight: 0,
  },
  clientSideWizardChildWrapper: {
    paddingLeft: 0,
    paddingRight: 0,
    justifyContent: 'flex-start',
  },
}));

type TransferStepperProps = {
  initialFundTransferFormData: Nullable<GenericObject>;
  toggleStepperIsActive: Function;
  service: TransferServiceImpl;
};
//TODO: use ClientSideWizardStepper
const TransferStepper: FC<TransferStepperProps> = ({ initialFundTransferFormData, toggleStepperIsActive, service }) => {
  const [stepStatus, setStepStatus] = useState({ completedSteps: 0, totalSteps: 0 });
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const navigate = useNavigate();
  const classes = useStyles();
  const { t } = useTranslation();

  const fundsTransferFlowConfig = useFundsTransferFlowConfig(
    toggleStepperIsActive,
    service,
    initialFundTransferFormData?.routingResponseData?.transferType,
  );
  const transferStepperConfig = useTransferStepperConfig(stepStatus.completedSteps);

  const onProcessEnd = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const onStepChange = useCallback((completedSteps, totalSteps) => {
    setStepStatus({ completedSteps, totalSteps });
  }, []);

  const finalFlowStepsNumber = Object.keys(fundsTransferFlowConfig.data).length;

  useEffect(() => {
    if (stepStatus.totalSteps === 0) {
      return;
    }
    if (stepStatus.completedSteps === stepStatus.totalSteps) {
      const pageTitle = document.getElementById('fund-transfer-page');
      if (pageTitle) {
        pageTitle.style.display = 'none';
      }
    }
  }, [stepStatus]);

  return (
    <>
      <ClientSideWizardStepper
        orientation='horizontal'
        stepperConfig={transferStepperConfig}
        totalSteps={stepStatus.totalSteps}
        totalStepsCompleted={stepStatus.completedSteps}
        hideInactiveLabelOnMobile={true}
        hideStepperIndicatorOnStepIndex={[finalFlowStepsNumber === 4 ? 3 : 2]}
        externalStepperWrapperClasses={classes.stepperWrapper}
        externalStepperClasses={classes.stepperRoot}
        ariaLabel={t('clientSideWizard.ClientSideWizardStepper.ariaLabel', {
          done: `${Math.max(0, stepStatus.completedSteps)}`,
          total: `${stepStatus.totalSteps}`,
        })}
      />
      <ClientSideWizardFlowPresenter
        processName={'fundsTransfer'}
        onStepChange={onStepChange}
        onProcessEnd={onProcessEnd}
        preloadedFormDataConfig={initialFormState(initialFundTransferFormData)}
        stepsConfig={fundsTransferFlowConfig.data}
        isTakeover={isMobile}
        shouldEnablePaddingBottom={false}
        rootContainerClasses={classes.clientSideWizardRootContainer}
        childContainerClasses={classes.clientSideWizardChildWrapper}
      />
    </>
  );
};

export { TransferStepper };
